import {useRef, useState} from 'react';
import {view, Icon, Divider, Tooltip, tokens} from '@sail/ui';
import {clipboard, link} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useWidget} from '../../../Widget/useWidget';

type PaymentLinkCardProps = {
  agentCallResponse: AgentCallResponse;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.payment_link_card.title',
    description: 'Payment Link Card title',
    defaultMessage: 'Payment link',
  },
  copyLink: {
    id: 'conversations_ui.payment_link_card.copy_link',
    description: 'Label for the copy link button',
    defaultMessage: 'Copy link',
  },
  copyLinkToast: {
    id: 'conversations_ui.payment_link_card.copy_link_toast',
    description: 'Notification of a successful copy from the copy link button',
    defaultMessage: 'Copied',
  },
});

export const PaymentLinkCard = ({agentCallResponse}: PaymentLinkCardProps) => {
  const intl = useIntl();
  const widget = useWidget();
  const copyLinkRef = useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const paymentLink = agentCallResponse.payment_link;
  const lineItems = agentCallResponse.line_items;

  if (!lineItems || lineItems.length === 0 || !paymentLink) {
    return null;
  }

  const {id, url} = paymentLink || {};
  const product = lineItems[0];
  const {
    id: productId,
    description: productName,
    amount_total: price,
    currency,
  } = product || {};

  return (
    <view.div
      css={{
        backgroundColor: 'surface',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'space.150',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        stack: 'y',
        gap: 'small',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/payment-links/${id}`,
          },
        });
      }}
    >
      <view.div
        css={{
          stack: 'x',
          gap: tokens.space[75].toString(),
          alignItems: 'center',
        }}
      >
        <Icon
          icon={link}
          size="xsmall"
          css={{color: isHovered ? 'action.primary' : 'subdued'}}
        />
        <view.span
          css={{
            color: 'subdued',
            font: 'label.small.emphasized',
          }}
        >
          {intl.formatMessage(messages.cardTitle)}
        </view.span>
        {productName ? (
          <view.div css={{height: 10, stack: 'x', width: 0}}>
            <Divider />
          </view.div>
        ) : null}
        {productName ? (
          <view.span
            css={{
              font: 'label.small.emphasized',
              color: 'action.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              ':hover': {
                color: 'action.primary.hovered',
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              widget.requestAction({
                name: 'navigate',
                data: {
                  href: `/products/${productId}`,
                },
              });
            }}
          >
            {productName}
          </view.span>
        ) : null}
      </view.div>
      {price && currency ? (
        <view.span
          css={{
            font: 'heading.large',
          }}
        >
          {intl.formatNumber(price / 100, {
            style: 'currency',
            currency,
          })}
        </view.span>
      ) : null}
      <Tooltip
        trigger={copyLinkRef}
        placement="top"
        css={{
          padding: 'small',
          color: 'onFeedback.neutral.emphasized',
        }}
      >
        {isCopied ? 'Copied!' : 'Copy link'}
      </Tooltip>
      <view.div
        onClick={(e) => {
          e.stopPropagation();
          widget.requestAction({
            name: 'copy-to-clipboard',
            data: {
              text: url,
            },
          });
          setIsCopied(true);
          setTimeout(() => {
            setIsCopied(false);
          }, 2000);
        }}
        css={{
          borderRadius: 'medium',
          padding: 'small',
          borderColor: '#ebeef1',
          borderWidth: 1,
          borderStyle: 'solid',
          backgroundColor: isCopied ? tokens.color.brand[50] : 'offset',
          display: 'flex',
          overflow: 'hidden',
          justifyContent: 'space-between',
          alignItems: 'center',
          ':hover': {
            cursor: 'pointer',
          },
        }}
      >
        <view.span
          css={{
            font: 'label.small.emphasized',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
            flexGrow: 1,
            flexShrink: 1,
          }}
        >
          {url}
        </view.span>
        <Icon
          ref={copyLinkRef}
          icon={clipboard}
          size="xsmall"
          css={{
            fill: 'action.primary',
            flexShrink: 0,
            marginLeft: 'space.50',
          }}
        />
      </view.div>
    </view.div>
  );
};
